export type MediaEdgeId = string;

export interface MediaEdgeSelection {
  id: MediaEdgeId;
  label: string;
  url: string;
  latencyMs: number;
  bandwidthMbps?: number;
  priority?: number;
}

interface MediaEdgeDefinition {
  id: MediaEdgeId;
  label: string;
  origin?: string;
}

interface MediaEdgeConfig {
  primary: MediaEdgeDefinition;
  alternates: MediaEdgeDefinition[];
}

interface EdgeMetric {
  averageMs: number;
  averageBitsPerSecond: number;
  failures: number;
  cooldownUntil: number;
}

const DEFAULT_PRIMARY_EDGE: MediaEdgeDefinition = {
  id: "primary",
  label: "链路",
};
const SAME_ORIGIN_EDGE_ID = "same-origin-relay";
const SAME_ORIGIN_EDGE_LABEL = "新 VPS 自动中继";
const METRIC_KEY = "kirkwat-media-edge-metrics-v1";
const SELECTION_CACHE_MS = 5 * 60_000;
const selectionCache = new Map<
  string,
  { expiresAt: number; value: MediaEdgeSelection }
>();
let memoryMetrics: Record<MediaEdgeId, EdgeMetric> | null = null;

const emptyMetric = (): EdgeMetric => ({
  averageMs: 0,
  averageBitsPerSecond: 0,
  failures: 0,
  cooldownUntil: 0,
});

function cleanText(value: unknown, fallback: string, maxLength = 80): string {
  if (typeof value !== "string") return fallback;
  const cleaned = value.replace(/[\u0000-\u001f\u007f]/g, " ").trim();
  return cleaned ? cleaned.slice(0, maxLength) : fallback;
}

function cleanOrigin(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed = new URL(value.trim());
    if (
      !["http:", "https:"].includes(parsed.protocol) ||
      !parsed.hostname ||
      parsed.username ||
      parsed.password ||
      parsed.search ||
      parsed.hash
    ) {
      return null;
    }
    const pathname = parsed.pathname.replace(/\/+$/, "");
    return `${parsed.protocol}//${parsed.host}${pathname}`;
  } catch {
    return null;
  }
}

function defaultAlternateEdges(): MediaEdgeDefinition[] {
  if (typeof window === "undefined") return [];
  try {
    const location = window.location;
    if (!/^https?:$/.test(location.protocol)) return [];
    const origin = cleanOrigin(`${location.origin}/tiktok/media-edge`);
    return origin
      ? [
          {
            id: SAME_ORIGIN_EDGE_ID,
            label: SAME_ORIGIN_EDGE_LABEL,
            origin,
          },
        ]
      : [];
  } catch {
    return [];
  }
}

function uniqueEdgeId(
  value: unknown,
  fallback: string,
  used: Set<string>,
): string {
  const base = cleanText(value, fallback, 100);
  let candidate = base;
  let suffix = 2;
  while (used.has(candidate)) {
    candidate = `${base}-${suffix}`;
    suffix += 1;
  }
  used.add(candidate);
  return candidate;
}

function readMediaEdgeConfig(): MediaEdgeConfig {
  const fallback: MediaEdgeConfig = {
    primary: { ...DEFAULT_PRIMARY_EDGE },
    alternates: defaultAlternateEdges(),
  };
  const raw =
    typeof process !== "undefined"
      ? process.env.EXPO_PUBLIC_MEDIA_EDGES_JSON
      : undefined;
  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw) as {
      version?: unknown;
      primary?: { id?: unknown; label?: unknown };
      alternates?: unknown;
    };
    if (!parsed || typeof parsed !== "object" || parsed.version !== 1) {
      return fallback;
    }

    const usedIds = new Set<string>();
    const primary: MediaEdgeDefinition = {
      id: uniqueEdgeId(parsed.primary?.id, DEFAULT_PRIMARY_EDGE.id, usedIds),
      label: cleanText(parsed.primary?.label, DEFAULT_PRIMARY_EDGE.label),
    };
    const alternates: MediaEdgeDefinition[] = [];
    const usedOrigins = new Set<string>();
    const sourceAlternates = Array.isArray(parsed.alternates)
      ? parsed.alternates.slice(0, 32)
      : [];

    sourceAlternates.forEach((value, index) => {
      if (!value || typeof value !== "object") return;
      const candidate = value as {
        id?: unknown;
        label?: unknown;
        origin?: unknown;
      };
      const origin = cleanOrigin(candidate.origin);
      if (!origin) return;
      const originKey = origin.toLowerCase();
      if (usedOrigins.has(originKey)) return;
      usedOrigins.add(originKey);
      const id = uniqueEdgeId(candidate.id, `alternate-${index + 1}`, usedIds);
      alternates.push({
        id,
        label: cleanText(candidate.label, `播放节点 ${index + 1}`),
        origin,
      });
    });

    return { primary, alternates };
  } catch (error) {
    console.warn("[MEDIA_EDGE_CONFIG_INVALID]", String(error));
    return fallback;
  }
}

const mediaEdgeConfig = readMediaEdgeConfig();

function normalizeMetric(value: unknown): EdgeMetric {
  const candidate =
    value && typeof value === "object"
      ? (value as Partial<Record<keyof EdgeMetric, unknown>>)
      : {};
  const finiteNumber = (item: unknown, fallback = 0) =>
    typeof item === "number" && Number.isFinite(item) ? item : fallback;
  return {
    averageMs: Math.max(0, finiteNumber(candidate.averageMs)),
    averageBitsPerSecond: Math.max(
      0,
      finiteNumber(candidate.averageBitsPerSecond),
    ),
    failures: Math.max(0, Math.min(10, finiteNumber(candidate.failures))),
    cooldownUntil: Math.max(0, finiteNumber(candidate.cooldownUntil)),
  };
}

function configuredEdges(): MediaEdgeDefinition[] {
  return [mediaEdgeConfig.primary, ...mediaEdgeConfig.alternates];
}

function readMetrics(): Record<MediaEdgeId, EdgeMetric> {
  if (memoryMetrics) return memoryMetrics;
  let saved: Record<string, unknown> = {};
  try {
    const raw =
      typeof window !== "undefined" &&
      typeof window.localStorage?.getItem === "function"
        ? window.localStorage.getItem(METRIC_KEY)
        : null;
    const parsed = raw ? JSON.parse(raw) : {};
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      saved = parsed as Record<string, unknown>;
    }
  } catch {}

  memoryMetrics = Object.create(null) as Record<MediaEdgeId, EdgeMetric>;
  configuredEdges().forEach((edge) => {
    memoryMetrics![edge.id] = normalizeMetric(saved[edge.id]);
  });
  return memoryMetrics;
}

function writeMetrics(metrics: Record<MediaEdgeId, EdgeMetric>) {
  memoryMetrics = metrics;
  try {
    if (
      typeof window !== "undefined" &&
      typeof window.localStorage?.setItem === "function"
    ) {
      window.localStorage.setItem(METRIC_KEY, JSON.stringify(metrics));
    }
  } catch {}
}

function isLocalMediaUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    const hostname = parsed.hostname.toLowerCase();
    return (
      /\/frp-media\//i.test(parsed.pathname) ||
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname.startsWith("192.168.")
    );
  } catch {
    return false;
  }
}

function alternateUrlFor(
  originalUrl: string,
  edge: MediaEdgeDefinition,
): string | null {
  if (!edge.origin || isLocalMediaUrl(originalUrl)) return null;
  try {
    const parsed = new URL(originalUrl);
    if (!["http:", "https:"].includes(parsed.protocol)) return null;
    const mediaIndex = parsed.pathname.indexOf("/media/");
    const edgePath = parsed.pathname.startsWith("/videos/")
      ? parsed.pathname
      : mediaIndex >= 0
      ? parsed.pathname.slice(mediaIndex)
      : "";
    if (!edgePath) return null;
    return `${edge.origin}${edgePath}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function rewriteMediaUrlToOrigin(
  originalUrl: string,
  edgeOrigin: string,
): string | null {
  const origin = cleanOrigin(edgeOrigin);
  if (!origin) return null;
  return alternateUrlFor(originalUrl, {
    id: "url-rewrite",
    label: "url-rewrite",
    origin,
  });
}

function urlMatchesOrigin(value: string, origin: string): boolean {
  try {
    const candidate = new URL(value);
    const base = new URL(origin);
    if (
      candidate.protocol.toLowerCase() !== base.protocol.toLowerCase() ||
      candidate.host.toLowerCase() !== base.host.toLowerCase()
    ) {
      return false;
    }
    const prefix = base.pathname.replace(/\/+$/, "");
    return (
      !prefix ||
      candidate.pathname === prefix ||
      candidate.pathname.startsWith(`${prefix}/`)
    );
  } catch {
    return false;
  }
}

function edgeForUrl(url: string): MediaEdgeDefinition {
  return mediaEdgeConfig.alternates.find(
    (edge) => edge.origin && urlMatchesOrigin(url, edge.origin),
  ) || mediaEdgeConfig.primary;
}

function edgeIdForUrl(url: string): MediaEdgeId {
  return edgeForUrl(url).id;
}

function playbackCandidates(
  originalUrl: string,
): Array<{ edge: MediaEdgeDefinition; url: string; priority: number }> {
  const originalEdge = edgeForUrl(originalUrl);
  const candidates: Array<{
    edge: MediaEdgeDefinition;
    url: string;
    priority: number;
  }> = [
    { edge: originalEdge, url: originalUrl, priority: 1 },
  ];
  const usedUrls = new Set([originalUrl]);
  mediaEdgeConfig.alternates.forEach((edge) => {
    const url = alternateUrlFor(originalUrl, edge);
    if (!url || usedUrls.has(url)) return;
    usedUrls.add(url);
    candidates.push({ edge, url, priority: candidates.length + 1 });
  });
  return candidates;
}

function prioritizedPlaybackCandidates(
  originalUrl: string,
  priorityIds: readonly string[] = [],
) {
  const candidates = playbackCandidates(originalUrl);
  const rank = new Map(priorityIds.map((id, index) => [id, index]));
  return [...candidates]
    .sort((left, right) => {
      const leftRank = rank.get(left.edge.id);
      const rightRank = rank.get(right.edge.id);
      if (leftRank !== undefined || rightRank !== undefined) {
        return (leftRank ?? Number.MAX_SAFE_INTEGER) -
          (rightRank ?? Number.MAX_SAFE_INTEGER);
      }
      return left.priority - right.priority;
    })
    .map((candidate, index) => ({ ...candidate, priority: index + 1 }));
}

export function mediaEdgeOptions(
  originalUrl: string,
  priorityIds: readonly string[] = [],
): MediaEdgeSelection[] {
  return prioritizedPlaybackCandidates(originalUrl, priorityIds).map(
    (candidate) => ({
      id: candidate.edge.id,
      label: mediaEdgeLabelForUrl(candidate.url),
      url: candidate.url,
      latencyMs: 0,
      priority: candidate.priority,
    }),
  );
}

export function mediaEdgeById(
  originalUrl: string,
  edgeId: string,
  priorityIds: readonly string[] = [],
): MediaEdgeSelection | null {
  return (
    mediaEdgeOptions(originalUrl, priorityIds).find(
      (candidate) => candidate.id === edgeId,
    ) || null
  );
}

export function nextMediaEdge(
  originalUrl: string,
  excludedUrls: Iterable<string>,
  priorityIds: readonly string[] = [],
): MediaEdgeSelection | null {
  const excluded = new Set(excludedUrls);
  const candidate = prioritizedPlaybackCandidates(originalUrl, priorityIds).find(
    ({ url }) => !excluded.has(url),
  );
  return candidate
    ? {
      id: candidate.edge.id,
      label: mediaEdgeLabelForUrl(candidate.url),
      url: candidate.url,
        latencyMs: 0,
        priority: candidate.priority,
      }
    : null;
}

async function probe(
  edge: MediaEdgeDefinition,
  url: string,
  priority: number,
  metric: EdgeMetric,
): Promise<{ selection: MediaEdgeSelection; score: number } | null> {
  if (metric.cooldownUntil > Date.now()) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 7000);

  const mediaPayloadUrl = async (): Promise<string> => {
    if (!/\.m3u8(?:$|[?#])/i.test(url)) return url;
    let playlistUrl = url;
    // A master playlist points at another playlist; a media playlist points at
    // the real segment. Follow at most two levels so the probe transfers media
    // bytes instead of timing only the small manifest response.
    for (let depth = 0; depth < 2; depth += 1) {
      const response = await fetch(playlistUrl, {
        cache: "no-store",
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.text();
      const relative = body
        .split(/\r?\n/)
        .map((line) => line.trim())
        .find((line) => line && !line.startsWith("#"));
      if (!relative) throw new Error("HLS playlist has no media URI");
      const resolved = new URL(relative, playlistUrl).toString();
      if (!/\.m3u8(?:$|[?#])/i.test(resolved)) return resolved;
      playlistUrl = resolved;
    }
    throw new Error("HLS playlist nesting is too deep");
  };

  try {
    const payloadUrl = await mediaPayloadUrl();
    const started = performance.now();
    const response = await fetch(payloadUrl, {
      method: "GET",
      headers: { Range: "bytes=0-524287" },
      cache: "no-store",
      signal: controller.signal,
    });
    const latencyMs = Math.max(1, Math.round(performance.now() - started));
    if (!response.ok) throw new Error(String(response.status));
    const reader = response.body?.getReader();
    let receivedBytes = 0;
    if (reader) {
      while (receivedBytes < 524288) {
        const { done, value } = await reader.read();
        if (done) break;
        receivedBytes += value?.byteLength || 0;
      }
      if (receivedBytes >= 524288) await reader.cancel().catch(() => {});
    } else {
      receivedBytes = (await response.arrayBuffer()).byteLength;
    }
    const elapsedMs = Math.max(1, performance.now() - started);
    const bitsPerSecond = (receivedBytes * 8 * 1000) / elapsedMs;
    if (receivedBytes < 16 * 1024 || bitsPerSecond <= 0) {
      throw new Error("media speed sample is too small");
    }
    const historicalMs = metric.averageMs || latencyMs;
    const historicalBitsPerSecond =
      metric.averageBitsPerSecond || bitsPerSecond;
    const effectiveBitsPerSecond =
      bitsPerSecond * 0.7 + historicalBitsPerSecond * 0.3;
    // Estimate how long this path needs to deliver an 8 Mbit playback chunk.
    // This keeps latency relevant while making real payload throughput the
    // dominant factor in node selection.
    const transferScoreMs = (8_000_000 / effectiveBitsPerSecond) * 1000;
    return {
      selection: {
        id: edge.id,
        label: mediaEdgeLabelForUrl(url),
        url,
        latencyMs,
        bandwidthMbps: Math.round((bitsPerSecond / 1_000_000) * 10) / 10,
        priority,
      },
      score:
        transferScoreMs +
        latencyMs * 0.35 +
        historicalMs * 0.15 +
        metric.failures * 350,
    };
  } catch (error) {
    console.log(`[MEDIA_EDGE_PROBE_FAILED] ${edge.id}`, String(error));
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export function primaryMediaEdgeLabel(): string {
  return mediaEdgeConfig.primary.label;
}

export function mediaEdgeLabelForUrl(value: string): string {
  if (/\/frp-media\//i.test(value)) return "等待 CDN 同步";
  if (isLocalMediaUrl(value)) return "本地";
  try {
    const parsed = new URL(value);
    if (/\.b-cdn\.net$/i.test(parsed.hostname)) return "Bunny CDN";
    if (/\/media-edge(?:\/|$)/i.test(parsed.pathname)) return "VPS 中继";
    if (/seaking\.vip$/i.test(parsed.hostname)) return "VPS 在线";
  } catch {}
  const alternate = mediaEdgeConfig.alternates.find(
    (edge) => edge.origin && urlMatchesOrigin(value, edge.origin),
  );
  if (alternate) return alternate.label;
  if (/^https?:\/\//i.test(value)) {
    try {
      return new URL(value).hostname;
    } catch {
      return primaryMediaEdgeLabel();
    }
  }
  return "网络服务器";
}

export async function selectMediaEdge(
  originalUrl: string,
  priorityIds: readonly string[] = [],
): Promise<MediaEdgeSelection> {
  const cacheKey = `${originalUrl}\n${priorityIds.join(",")}`;
  const cached = selectionCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  const candidates = prioritizedPlaybackCandidates(originalUrl, priorityIds);

  if (candidates.length === 1) {
    return {
      id: edgeIdForUrl(originalUrl),
      label: mediaEdgeLabelForUrl(originalUrl),
      url: originalUrl,
      latencyMs: 0,
      priority: 1,
    };
  }

  const metrics = readMetrics();
  // Candidate order is the default playback priority. Probe sequentially and
  // stop at the first usable node; alternates are fallbacks, not competitors
  // that need to be re-tested for every video.
  let chosenResult: Awaited<ReturnType<typeof probe>> = null;
  for (const candidate of candidates) {
    const metric = metrics[candidate.edge.id] || emptyMetric();
    metrics[candidate.edge.id] = metric;
    if (metric.cooldownUntil > Date.now()) continue;
    const result = await probe(
      candidate.edge,
      candidate.url,
      candidate.priority,
      metric,
    );
    if (result) {
      chosenResult = result;
      break;
    }
    metric.failures = Math.min(10, metric.failures + 1);
    metric.cooldownUntil =
      Date.now() + Math.min(30 * 60_000, metric.failures * 2 * 60_000);
    writeMetrics(metrics);
  }
  const results = chosenResult ? [chosenResult] : [];
  if (!results.length) {
    return {
      id: mediaEdgeConfig.primary.id,
      label: mediaEdgeLabelForUrl(originalUrl),
      url: originalUrl,
      latencyMs: 0,
      priority: 1,
    };
  }

  results.sort((left, right) => left.score - right.score);
  const chosen = results[0].selection;
  const metric = metrics[chosen.id];
  metric.averageMs = metric.averageMs
    ? Math.round(metric.averageMs * 0.7 + chosen.latencyMs * 0.3)
    : chosen.latencyMs;
  const chosenBitsPerSecond = (chosen.bandwidthMbps || 0) * 1_000_000;
  if (chosenBitsPerSecond > 0) {
    metric.averageBitsPerSecond = metric.averageBitsPerSecond
      ? Math.round(
          metric.averageBitsPerSecond * 0.7 + chosenBitsPerSecond * 0.3,
        )
      : chosenBitsPerSecond;
  }
  metric.failures = Math.max(0, metric.failures - 1);
  metric.cooldownUntil = 0;
  writeMetrics(metrics);
  selectionCache.set(cacheKey, {
    expiresAt: Date.now() + SELECTION_CACHE_MS,
    value: chosen,
  });
  return chosen;
}

export function reportMediaEdgeFailure(url: string): void {
  const id = edgeIdForUrl(url);
  const metrics = readMetrics();
  const metric = metrics[id] || emptyMetric();
  metric.failures = Math.min(10, metric.failures + 1);
  metric.cooldownUntil =
    Date.now() + Math.min(30 * 60_000, metric.failures * 2 * 60_000);
  metrics[id] = metric;
  writeMetrics(metrics);
  selectionCache.clear();
}
